import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { temPapel, useAuth } from "../../lib/auth";
import { ITENS_MENU, telaVisivel } from "../../lib/menu";
import { fmtDataHora, fmtNum, nomeTipoLocal } from "../../lib/formato";
import { Botao, Campo, Secao, Selo, Tabela, Vazio } from "../../componentes/ui";
import { PedirReposicao } from "../../componentes/PedirReposicao";
import { DetalhePosicao } from "../../componentes/DetalhePosicao";

interface LinhaSaldo {
  quantidade: number;
  reservado: number;
  locais: { id: string; codigo: string; tipo: string; bloqueado: boolean };
  produtos: { codigo: string | null; nome: string };
}

/** O que o estoquista faz com o estoque, cada um com a sua tela (nomes e descrições do menu). */
const ACOES = ["/recebimento/armazenar", "/estoque/transferir", "/estoque/inventario", "/estoque/entrada"];

export default function Estoque() {
  const { perfil } = useAuth();
  const [termo, setTermo] = useState("");
  const [busca, setBusca] = useState("");
  const [aberta, setAberta] = useState<{ id: string; codigo: string } | null>(null);

  const resultado = useQuery({
    queryKey: ["estoque", busca],
    enabled: !!busca,
    queryFn: async () => {
      const campos = "quantidade, reservado, locais!inner(id, codigo, tipo, bloqueado), produtos!inner(codigo, nome)";
      const [porLocal, porProduto, porNome] = await Promise.all([
        supabase.from("saldo_posicao").select(campos).eq("locais.codigo", busca.toUpperCase()).gt("quantidade", 0),
        supabase.from("saldo_posicao").select(campos).eq("produtos.codigo", busca).gt("quantidade", 0),
        supabase.from("saldo_posicao").select(campos).ilike("produtos.nome", `%${busca}%`).gt("quantidade", 0).limit(200),
      ]);
      const linhas = [...(porLocal.data ?? []), ...(porProduto.data ?? []), ...(porNome.data ?? [])] as unknown as LinhaSaldo[];
      const vistas = new Set<string>();
      return linhas.filter((l) => {
        const k = `${l.locais.codigo}|${l.produtos.codigo}`;
        if (vistas.has(k)) return false;
        vistas.add(k);
        return true;
      });
    },
  });

  const inventarios = useQuery({
    queryKey: ["estoque", "inventarios"],
    queryFn: async () => {
      const { data } = await supabase.from("tarefas_inventario")
        .select("id, motivo, criado_em, locais(codigo)").eq("status", "pendente").order("criado_em");
      return (data ?? []) as unknown as Array<{ id: string; motivo: string; criado_em: string; locais: { codigo: string } }>;
    },
  });

  const acoes = ACOES.map((to) => ITENS_MENU.find((i) => i.to === to)!)
    .filter((i) => i && temPapel(perfil, ...i.papeis) && telaVisivel(perfil, i.to));
  const paraGuardar = useQuery({
    queryKey: ["estoque", "para-guardar"],
    enabled: acoes.some((i) => i.to === "/recebimento/armazenar"),
    queryFn: async () => {
      const { count } = await supabase.from("v_doca_pendente").select("produto_id", { count: "exact", head: true });
      return count ?? 0;
    },
    refetchInterval: 60_000,
  });

  function buscar(e: FormEvent) {
    e.preventDefault();
    setBusca(termo.trim());
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-sinal text-3xl font-bold">Consultar estoque</h1>

      <form onSubmit={buscar} className="flex flex-wrap items-end gap-2">
        <Campo rotulo="Posição, SKU ou parte do nome" value={termo} onChange={(e) => setTermo(e.target.value)} className="min-w-72 flex-1" />
        <Botao type="submit">Consultar</Botao>
      </form>

      {aberta && <DetalhePosicao localId={aberta.id} codigo={aberta.codigo} onFechar={() => setAberta(null)} />}
      {busca && (
        <Secao titulo={`Resultado para "${busca}"`}>
          {resultado.data?.length ? (
            <Tabela cabecalho={["Local", "Tipo", "Produto", "Saldo", "Reservado"]}>
              {resultado.data.map((l, i) => (
                <tr key={i}>
                  <td className="font-sinal text-lg font-semibold">
                    <button type="button" className="underline" onClick={() => setAberta({ id: l.locais.id, codigo: l.locais.codigo })}>{l.locais.codigo}</button>
                    {" "}{l.locais.bloqueado && <Selo tom="alerta">bloqueado</Selo>}
                  </td>
                  <td>{nomeTipoLocal[l.locais.tipo] ?? l.locais.tipo}</td>
                  <td>{l.produtos.codigo} <span className="text-aco-escuro">{l.produtos.nome}</span></td>
                  <td className="font-semibold">{fmtNum(l.quantidade)}</td>
                  <td>{l.reservado > 0 ? fmtNum(l.reservado) : ""}</td>
                </tr>
              ))}
            </Tabela>
          ) : <Vazio>{resultado.isLoading ? "Consultando..." : "Nada encontrado com saldo."}</Vazio>}
        </Secao>
      )}

      {!!inventarios.data?.length && (
        <Secao titulo="Posições aguardando inventário">
          <ul className="divide-y divide-aco">
            {inventarios.data.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="font-sinal text-xl font-bold">{t.locais?.codigo}</span>
                <span className="flex-1 text-aco-escuro">{t.motivo}</span>
                <span className="text-sm">{fmtDataHora(t.criado_em)}</span>
              </li>
            ))}
          </ul>
        </Secao>
      )}

      <Secao titulo="O que você vai fazer?">
        <div className="grid gap-2 sm:grid-cols-2">
          {acoes.map((i) => (
            <Link key={i.to} to={i.to} className="flex min-h-16 items-start gap-3 rounded-lg border-2 border-aco bg-chapa px-4 py-3 hover:border-tinta">
              <i.icone aria-hidden className="mt-0.5 size-6 shrink-0" strokeWidth={2.25} />
              <span>
                <span className="flex flex-wrap items-center gap-2 text-lg font-semibold">
                  {i.titulo}
                  {i.to === "/recebimento/armazenar" && !!paraGuardar.data && <Selo tom="faixa">{paraGuardar.data} produto(s) para guardar</Selo>}
                </span>
                <span className="block text-sm text-aco-escuro">{i.texto}</span>
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-3"><PedirReposicao /></div>
      </Secao>
    </div>
  );
}
